import { lazy } from 'react';

import { LayoutType, PageType } from '@shared/constants/constants';
import {
  ACTION_BACK_PATH,
  AUTOPAY_ADD_CARD_DETAILS_PATH,
  AUTOPAY_ADD_PARTICULARS_PATH,
  AUTOPAY_ADD_SELECT_INSTITUTION_PATH,
  AUTOPAY_ADD_SELECT_PATIENT_PATH,
  AUTOPAY_CARD_UPDATE_STATUS_PATH,
  AUTOPAY_MANAGE_CARD_PATH,
  AUTOPAY_PATH,
  AUTOPAY_PATIENT_DETAILS_PATH,
  AUTOPAY_PATIENT_SETUP_STATUS_PATH,
  AUTOPAY_TERMS_AND_CONDITION_DETAILS_PATH,
  AUTOPAY_TERMS_AND_CONDITION_PATH,
  AUTOPAY_VERIFY_PAYOR_PATH,
  AUTOPAY_VERIFY_PAYOR_SUMMARY_PATH,
  AUTOPAY_VIEW_CARD_LIST_PATH,
  AUTOPAY_VIEW_SELECT_INSTITUTION_PATH,
  AUTOPAY_VIEW_SELECT_PATIENT_PATH,
} from '@shared/constants/paths';
import { SERVICE_CATALOG_CODE } from '@shared/constants/serviceCatalog';
import type { RouteConfig } from '@shared/types/common.types';

const AutopayPage = lazy(() => import('./pages/AutopayPage'));
const SelectInstitutionPage = lazy(() => import('./pages/SelectInstitutionPage'));
const TermsAndConditionPage = lazy(() => import('./pages/TermsAndConditionPage'));
const TermsAndConditionDetailsPage = lazy(() => import('./pages/TermsAndConditionDetailsPage'));
const SelectPatientPage = lazy(() => import('./pages/SelectPatientPage'));
const PatientDetailsPage = lazy(() => import('./pages/PatientDetailsPage'));
const AddParticularsPage = lazy(() => import('./pages/AddParticularPage'));
const VerifyPayorPage = lazy(() => import('./pages/VerifyPayorPage'));
const VerifyPayorSummaryPage = lazy(() => import('./pages/VerifyPayorSummaryPage'));
const AddCardDetailsPage = lazy(() => import('./pages/AddCardDetailsPage'));
const AutopaySetupStatusPage = lazy(() => import('./pages/AutopaySetupStatusPage'));
const ViewCardListPage = lazy(() => import('./pages/ViewCardListPage'));
const ManageCardPage = lazy(() => import('./pages/ManageCardPage'));
const AutopayCardUpdatedStatusPage = lazy(() => import('./pages/AutopayCardUpdatedStatusPage'));

export const AUTOPAY_ROUTES: RouteConfig[] = [
  {
    actionUrl: ACTION_BACK_PATH,
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE],
    component: () => <AutopayPage />,
    displayTitle: () => 'Auto-Pay Setup',
    pageType: PageType.PRIVATE,
    path: AUTOPAY_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.autoPaySetup,
  },
  // Add auto pay
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE],
    backUrl: AUTOPAY_PATH,
    component: () => <SelectInstitutionPage />,
    displayTitle: () => 'Add New Auto-Pay',
    pageType: PageType.PRIVATE,
    path: AUTOPAY_ADD_SELECT_INSTITUTION_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.autoPaySetup,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE],
    component: () => <TermsAndConditionPage />,
    displayTitle: () => 'Add New Auto-Pay',
    pageType: PageType.PRIVATE,
    path: AUTOPAY_TERMS_AND_CONDITION_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.autoPaySetup,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE],
    component: () => <TermsAndConditionDetailsPage />,
    displayTitle: () => 'Add New Auto-Pay',
    pageType: PageType.PRIVATE,
    path: AUTOPAY_TERMS_AND_CONDITION_DETAILS_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.autoPaySetup,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE],
    component: () => <SelectPatientPage />,
    displayTitle: () => 'Add New Auto-Pay',
    pageType: PageType.PRIVATE,
    path: AUTOPAY_ADD_SELECT_PATIENT_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.autoPaySetup,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE],
    component: () => <PatientDetailsPage />,
    displayTitle: () => 'Add New Auto-Pay',
    pageType: PageType.PRIVATE,
    path: AUTOPAY_PATIENT_DETAILS_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.autoPaySetup,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE],
    component: () => <AddParticularsPage />,
    displayTitle: () => 'Add New Auto-Pay',
    pageType: PageType.PRIVATE,
    path: AUTOPAY_ADD_PARTICULARS_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.autoPaySetup,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE],
    component: () => <VerifyPayorPage />,
    displayTitle: () => 'Add New Auto-Pay',
    pageType: PageType.PRIVATE,
    path: AUTOPAY_VERIFY_PAYOR_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.autoPaySetup,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE],
    component: () => <VerifyPayorSummaryPage />,
    displayTitle: () => 'Add New Auto-Pay',
    pageType: PageType.PRIVATE,
    path: AUTOPAY_VERIFY_PAYOR_SUMMARY_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.autoPaySetup,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE],
    component: () => <AddCardDetailsPage />,
    displayTitle: () => 'Add New Auto-Pay',
    pageType: PageType.PRIVATE,
    path: AUTOPAY_ADD_CARD_DETAILS_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.autoPaySetup,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE],
    backUrl: AUTOPAY_PATH,
    component: () => <AutopaySetupStatusPage />,
    displayTitle: () => 'Add New Auto-Pay',
    pageType: PageType.PRIVATE,
    path: AUTOPAY_PATIENT_SETUP_STATUS_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.autoPaySetup,
  },
  // View auto pay
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE],
    backUrl: AUTOPAY_PATH,
    component: () => <SelectInstitutionPage isViewAutopayCardSetup />,
    displayTitle: () => 'Auto-Pay Setup',
    pageType: PageType.PRIVATE,
    path: AUTOPAY_VIEW_SELECT_INSTITUTION_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.autoPaySetup,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE],
    backUrl: AUTOPAY_VIEW_SELECT_INSTITUTION_PATH,
    component: () => <SelectPatientPage isViewAutopayCardSetup />,
    displayTitle: () => 'Auto-Pay Setup',
    pageType: PageType.PRIVATE,
    path: AUTOPAY_VIEW_SELECT_PATIENT_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.autoPaySetup,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE],
    backUrl: AUTOPAY_VIEW_SELECT_PATIENT_PATH,
    component: () => <ViewCardListPage />,
    displayTitle: () => 'Auto-Pay Setup Details',
    pageType: PageType.PRIVATE,
    path: AUTOPAY_VIEW_CARD_LIST_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.autoPaySetup,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE],
    component: () => <ManageCardPage />,
    displayTitle: () => 'Manage Card',
    pageType: PageType.PRIVATE,
    path: AUTOPAY_MANAGE_CARD_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.autoPaySetup,
  },
  {
    availableViews: [LayoutType.PORTAL, LayoutType.MOBILE],
    backUrl: AUTOPAY_PATH,
    component: () => <AutopayCardUpdatedStatusPage />,
    displayTitle: () => 'Auto-Pay Setup Details',
    pageType: PageType.PRIVATE,
    path: AUTOPAY_CARD_UPDATE_STATUS_PATH,
    serviceCatalogCode: SERVICE_CATALOG_CODE.autoPaySetup,
  },
];
